import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import base64 from 'base-64';
import utf8 from 'utf8';
import { z } from 'zod';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

import { ModalProps, Secret } from '@typings';
import { toast } from '@features';
import { createSecret, getSecrets } from '@services/secret.services';
import { clusterContextSelector, contextNamesSelector } from '@selectors';
import { useFetchList, useSelector } from '@hooks';
import {
  as,
  cn,
  invariant,
  noop,
  normalizeFormErrors,
  toastifyResponseError,
} from '@utils';

import { Button, ChaseSpinner, Field, Icon, Modal, Render } from '@components';

type Schema = z.infer<typeof schema>;

const schema = z
  .object({
    isCreatingSecret: z.boolean().optional(),
    secretKey: z.string().optional(),
    name: z.string().optional(),
    value: z.string().optional(),
  })
  .superRefine(({ isCreatingSecret, secretKey, name, value }, context) => {
    if (isCreatingSecret) {
      if (!name) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['name'],
          message: 'Secret name is required',
        });
      }

      if (!value) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['value'],
          message: 'Secret value is required',
        });
      }
    }

    if (!isCreatingSecret && !secretKey) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['secretKey'],
        message: 'Required',
      });

      toast.warning('Please select a secret');
    }
  });

type Props = ModalProps & {
  onApply?: (secretKey: string) => void;
};

export const ManageSecretsModal = ({
  onApply = noop,
  closeModal = noop,
}: Props) => {
  const cluster = useSelector(clusterContextSelector);
  const { organizationName, projectName } = useSelector(contextNamesSelector);

  const {
    isFetched,
    isEmpty,
    list: secrets,
    getList,
  } = useFetchList<Secret[]>({
    getList: getSecrets,
    fetchOnMount: false,
  });

  const { formState, register, setValue, resetField, watch, handleSubmit } =
    useForm<Schema>({
      resolver: zodResolver(schema),
      defaultValues: {
        secretKey: '',
        isCreatingSecret: false,
      },
    });

  const [loading, setLoading] = useState(false);

  const isCreatingSecret = watch('isCreatingSecret');
  const errors = normalizeFormErrors<keyof Schema>(formState.errors);

  const { secretsUrl } = as.c(cluster);
  const noSecretsFound = isFetched && isEmpty;

  useEffect(() => {
    setValue('isCreatingSecret', noSecretsFound);
  }, [noSecretsFound, setValue]);

  useEffect(() => {
    const getSecretList = async () => {
      try {
        await getList({ secretsUrl, organizationName, projectName });
      } catch (error) {
        toastifyResponseError(error);
      }
    };

    getSecretList();
  }, [organizationName, projectName, secretsUrl, getList]);

  const handleSecretApply = handleSubmit(
    async ({ isCreatingSecret, name, secretKey, value }) => {
      try {
        if (!isCreatingSecret) {
          invariant(secretKey);

          onApply(secretKey);
          closeModal();

          return;
        }

        setLoading(true);

        invariant(projectName);
        invariant(organizationName);
        invariant(name);
        invariant(value);

        const formattedKey = name.replace(/\s/g, '');

        const { key } = await createSecret({
          secretsUrl,
          key: formattedKey,
          value: base64.encode(utf8.encode(value)),
          organizationName,
          projectName,
        });

        toast.success('The secret is created');

        onApply(key);
        closeModal();
      } catch (error) {
        toastifyResponseError(error);
      } finally {
        setLoading(false);
      }
    },
  );

  const handleSecretCreatingChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    resetField('secretKey');
    setValue('isCreatingSecret', event.target.checked);
  };

  const handleSecretChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    resetField('isCreatingSecret');
    setValue('secretKey', event.target.value);
  };

  const makeSecret = ({ key }: Secret) => (
    <Field.Radio
      {...register('secretKey')}
      key={key}
      value={key}
      onChange={handleSecretChange}
    >
      {key}
    </Field.Radio>
  );

  const renderSecretsContent = () => {
    if (!isFetched) {
      return (
        <div className="relative flex min-h-[400px] w-full items-center justify-center">
          <ChaseSpinner color="black" className="h-12 w-12" />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <Render if={secrets}>
          <div className="flex max-h-52 flex-col gap-6 overflow-y-scroll">
            {secrets.map(makeSecret)}
          </div>
          <div className="my-6 h-px bg-neural-02" />
          <Field.Checkbox
            {...register('isCreatingSecret')}
            variant="radio"
            containerClassName={clsx({ 'mb-6': isCreatingSecret })}
            onChange={handleSecretCreatingChange}
          >
            Create new secret
          </Field.Checkbox>
        </Render>
        <Render if={noSecretsFound}>
          <p className="mb-8 flex items-center gap-2">
            <Icon
              icon={faTriangleExclamation}
              className="inline-flex text-[20px] text-warning"
            />{' '}
            No secrets yet. Start by creating a new one
          </p>
          <p className="mb-3 text-h6">Create new secret</p>
        </Render>
        <Render if={isCreatingSecret || secrets.length === 0}>
          <Field.Input {...register('name')} label="Name" error={errors.name} />
          <Field.Input
            {...register('value')}
            label="Value"
            error={errors.value}
          />
        </Render>
        <Modal.Footer className="px-0">
          <Button variant="secondary" className="px-6" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            loading={loading}
            className={cn('px-8', { 'px-12': !isCreatingSecret })}
            onClick={handleSecretApply}
          >
            {isCreatingSecret ? 'Create and Apply' : 'Apply'}
          </Button>
        </Modal.Footer>
      </div>
    );
  };

  return (
    <Modal.Content title="Environment Secrets" className="w-[640px]">
      {renderSecretsContent()}
    </Modal.Content>
  );
};
